import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsComparedGroupedAggregate, SalesInsightsGroupedAggregateItem, useSalesInsightsCompareGroupedAggregated } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import LineBarChartCompared, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChartCompared';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromComparedRanges } from '../../dashboardHelpers';

const ComparedNetMarginLocation = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { refreshDate } = useContext(DashboardContext);

    const initialStartDate = filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialEndDate = filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    const initialCompareStart = filters.comparePeriodFrom
        ? filters.comparePeriodFrom
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialCompareEnd = filters.comparePeriodTo
        ? filters.comparePeriodTo
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netMargin',
        groupBy: 'location',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        compareStartDate: formatDateParam(initialCompareStart),
        compareEndDate: formatDateParam(initialCompareEnd),
        locations: filters?.locations?.join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromComparedRanges(
            initialStartDate,
            initialEndDate,
            initialCompareStart,
            initialCompareEnd
        )
    });

    const {
        data: groupedAggregateSalesResponse,
        isLoading,
        isError,
        refetch: fetchSalesInsightsGrouped
    } = useSalesInsightsCompareGroupedAggregated<SalesInsightsComparedGroupedAggregate>(params, 'net-margin-by-location-grouped-aggregate');

    useEffect(() => {
        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        const compareStart = filters.comparePeriodFrom
            ? filters.comparePeriodFrom
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

        const compareEnd = filters.comparePeriodTo
            ? filters.comparePeriodTo
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

        const bucket = getBucketFromComparedRanges(
            start,
            end,
            compareStart,
            compareEnd
        );
        setParams({
            ...params,
            startDate: formatDateParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            endDate: formatDateParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
            compareStartDate: formatDateParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDate: formatDateParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsightsGrouped() }, [params]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsightsGrouped();
        }
    }, [refreshDate]);

    function transformData(dataItem: { dateTime: string } & SalesInsightsGroupedAggregateItem): LineBarChartDataItem {
        const result: LineBarChartDataItem = {
            dateTime: new Date(dataItem.dateTime),
            barData: 0
        };

        dataItem?.salesInsightsGrouped?.forEach(({ key, salesInsights }) => {
            result[key] = salesInsights?.netMargin ?? 0;
            result.barData += salesInsights?.transactionVolume ?? 0;
        });
        return result;
    }

    return (
        <LineBarChartCompared
            title={t("screens.dashboarding.charts.netMarginByLocation")}
            currency={groupedAggregateSalesResponse?.currency}
            description={t("screens.dashboarding.charts.excludingTax")}
            data={groupedAggregateSalesResponse?.salesInsightsGroupedAggregate?.map(transformData) ?? []}
            isLoading={isLoading}
            isError={isError}
            rightAxisLabel={t("screens.dashboarding.charts.volume")}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            barLabel='Volume'
            bucket={params.bucket as Bucket}
            setBucket={(newView: Bucket) => setParams({ ...params, bucket: newView })}
            height={460}
            dataCompared={groupedAggregateSalesResponse?.comparedSalesInsightsGroupedAggregate?.map(transformData) ?? []}
            currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
            currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
            comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
            comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
            textLink={t('screens.dashboarding.charts.salesByLocation')}
            linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByLocation.aspx`}
        />
    )
}

export default ComparedNetMarginLocation;
